import { Button } from '@mui/material';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { fundsRequestsApi, shopsApi } from 'api';
import { DataGridColumnDefinition, ProcessedByInfo } from 'components';
import { FundsRequestStatus, QueryKey } from 'enums';
import { FundsRequests } from 'features/funds-request';
import { useMutation, usePartialQuery, useShopsQuery, useUser } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { FundsRequest } from 'types';

type Props = {
  active?: boolean;
};

export const MyFundsRequests: React.FC<Props> = ({ active }) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.funds_request',
  });
  const { isMerchant } = useUser();

  const { data: shops = isMerchant ? [] : undefined } = useShopsQuery(
    QueryKey.Shops,
    shopsApi.getAllAsMerchant,
    { enabled: isMerchant },
  );

  const queryResult = usePartialQuery(
    QueryKey.MyFundsRequests,
    active
      ? fundsRequestsApi.getMyActivePaginated
      : fundsRequestsApi.getMyArchivePaginated,
  );

  const queryClient = useQueryClient();

  const { mutate: cancel } = useMutation(fundsRequestsApi.revoke, {
    onSuccess: () => {
      queryClient.invalidateQueries(QueryKey.MyFundsRequests);
    },
  });

  const additionalColumns = useMemo(
    (): DataGridColumnDefinition<FundsRequest>[] => [
      {
        header: t('funds_requests.fields.actions'),
        valueGetter: (item) =>
          item.status === FundsRequestStatus.New && (
            <div>
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={() => cancel(item)}
              >
                {t('funds_requests.fields.revoke')}
              </Button>
            </div>
          ),
        hidden: !active,
      },
      {
        valueGetter: (item: FundsRequest) => (
          <ProcessedByInfo
            processor={item.processor}
            processedAt={item.processedAt}
          />
        ),
        hidden: active,
      },
    ],
    [cancel, active, t],
  );

  return (
    <FundsRequests
      archived={!active}
      queryResult={queryResult}
      additionalColumns={additionalColumns}
      shops={shops}
      hideTitle
    />
  );
};
